import { Trans } from "@lingui/react/macro"
import { Group, Menu } from "@mantine/core"
import {
  IconBuildingSkyscraper,
  IconChevronDown,
  IconClipboardList,
  IconCoins,
  IconLayoutGrid,
  IconMicrophone,
  IconReportMoney,
  IconSearch,
  IconSettings,
  IconSitemap,
  IconSparkles,
  IconUsers,
  IconUsersGroup,
  IconVocabulary,
} from "@tabler/icons-react"
import { Link, useLocation } from "@tanstack/react-router"
import { useProduct } from "../../../lib/tenant"

interface INavLinksProps {
  isAdmin: boolean
  hasOrg: boolean
  /** Whether to show the Audit log link — super-admins, or org owners/admins. */
  canViewAudit: boolean
  /** Active org id — scopes the parametered Speakers manage link. Null when a
   * super-admin browses with no active org (they reach Speakers via the
   * company-detail page instead). */
  activeOrgId?: string | null
  /** Hide Ask AI and Jargon in lite mode. Credits stay — transcription still spends credits. */
  isLite?: boolean
  /** Called after a nav link click — used to close the mobile drawer. */
  onNavigate?: () => void
  /** "horizontal" inside the header at >=md, "vertical" inside the drawer at <md. */
  layout?: "horizontal" | "vertical"
}

/** Static, param-less route targets used by the nav. Narrow union (not the full
 * router `to` type) so `<Link to={item.to}>` never demands `params`. */
type TNavPath =
  | "/"
  | "/search"
  | "/ask"
  | "/usage"
  | "/jargon"
  | "/campaigns"
  | "/members"
  | "/companies"
  | "/tenant-usage"
  | "/pipeline"
  | "/settings"
  | "/provider-credits"
  | "/platform-billing"
  | "/pipeline-runs"
  | "/audit"
  | "/speakers"

interface INavItemBase {
  /** Kept as a `<Trans>` element so `lingui extract` still statically sees the string. */
  label: React.ReactNode
  /** Leading icon — Lexicanon only; the Voxcanon nav renders labels bare. */
  icon?: React.ReactNode
  /** Whether this link shows for the current user/flags. */
  visible: boolean
  /** Section the row sits in inside the mobile drawer: "workspace" for
   * everyday member surfaces, "admin" for super-admin / owner tooling (shown
   * under an "Administration" eyebrow). The horizontal header ignores this and
   * renders one flat row. */
  group: "workspace" | "admin"
}

/** A nav destination. Every nav target is param-less — `<Link to={item.to}>`
 * never demands `params`, which is why `TNavPath` stays a narrow param-less
 * union. */
interface INavItem extends INavItemBase {
  to: TNavPath
}

export function NavLinks({
  isAdmin,
  hasOrg,
  canViewAudit,
  activeOrgId,
  isLite = false,
  onNavigate,
  layout = "horizontal",
}: INavLinksProps): React.JSX.Element | null {
  const product = useProduct()
  const { pathname } = useLocation()
  if (!isAdmin && !hasOrg) return null

  const handleNav = (): void => {
    onNavigate?.()
  }

  // Links visible to any member of the active org, or to a super-admin.
  const canOrg = isAdmin || hasOrg
  const isVox = product === "voxcanon"
  // On the admin host with no company, "/" renders the companies list. A
  // "Meetings" link there would be a second way into Companies, and it would
  // stay highlighted on every admin page. Companies takes that first slot.
  const companiesFirst = isAdmin && !hasOrg

  // ONE data-driven list per product — the header (horizontal) and the mobile
  // drawer (vertical, sectioned) both render from it, so the two never drift.
  // `group` only affects the drawer, which files "admin" rows under an
  // "Administration" eyebrow. Voxcanon: no icons, `campaigns` + order are
  // Voxcanon-specific. Lexicanon: icon + label, `campaigns` omitted (redirects
  // on this host), `members` + `provider-credits` are Lexicanon-specific.
  const baseItems: INavItem[] = isVox
    ? [
        { to: "/campaigns", label: <Trans>Campaigns</Trans>, visible: hasOrg, group: "workspace" },
        { to: "/", label: <Trans>Sessions</Trans>, visible: hasOrg, group: "workspace" },
        { to: "/search", label: <Trans>Search</Trans>, visible: hasOrg, group: "workspace" },
        { to: "/ask", label: <Trans>Ask AI</Trans>, visible: canOrg && !isLite, group: "workspace" },
        { to: "/usage", label: <Trans>Credits</Trans>, visible: canOrg, group: "workspace" },
        { to: "/jargon", label: <Trans>Jargon</Trans>, visible: canOrg && !isLite, group: "workspace" },
        { to: "/companies", label: <Trans>Companies</Trans>, visible: isAdmin && !companiesFirst, group: "admin" },
        { to: "/tenant-usage", label: <Trans>Tenant usage</Trans>, visible: isAdmin, group: "admin" },
        { to: "/platform-billing", label: <Trans>Platform billing</Trans>, visible: isAdmin, group: "admin" },
        { to: "/pipeline", label: <Trans>Pipeline</Trans>, visible: isAdmin, group: "admin" },
        { to: "/pipeline-runs", label: <Trans>Pipeline runs</Trans>, visible: isAdmin, group: "admin" },
        { to: "/settings", label: <Trans>Settings</Trans>, visible: isAdmin, group: "admin" },
        { to: "/audit", label: <Trans>Audit log</Trans>, visible: canViewAudit, group: "admin" },
      ]
    : [
        {
          to: "/",
          label: <Trans>Meetings</Trans>,
          icon: <IconLayoutGrid size={16} className="lex-nav-ico" aria-hidden />,
          visible: hasOrg,
          group: "workspace",
        },
        {
          to: "/search",
          label: <Trans>Search</Trans>,
          icon: <IconSearch size={16} className="lex-nav-ico" aria-hidden />,
          visible: hasOrg,
          group: "workspace",
        },
        {
          to: "/ask",
          label: <Trans>Ask AI</Trans>,
          icon: <IconSparkles size={16} className="lex-nav-ico" aria-hidden />,
          visible: canOrg && !isLite,
          group: "workspace",
        },
        {
          to: "/usage",
          label: <Trans>Credits</Trans>,
          icon: <IconCoins size={16} className="lex-nav-ico" aria-hidden />,
          visible: canOrg,
          group: "workspace",
        },
        {
          to: "/jargon",
          label: <Trans>Jargon</Trans>,
          icon: <IconVocabulary size={16} className="lex-nav-ico" aria-hidden />,
          visible: canOrg && !isLite,
          group: "workspace",
        },
        {
          to: "/members",
          label: <Trans>Members</Trans>,
          icon: <IconUsers size={16} className="lex-nav-ico" aria-hidden />,
          visible: hasOrg,
          group: "workspace",
        },
        {
          to: "/companies",
          label: <Trans>Companies</Trans>,
          icon: <IconBuildingSkyscraper size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin && !companiesFirst,
          group: "admin",
        },
        {
          to: "/tenant-usage",
          label: <Trans>Tenant usage</Trans>,
          icon: <IconUsersGroup size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/pipeline",
          label: <Trans>Pipeline</Trans>,
          icon: <IconSitemap size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/pipeline-runs",
          label: <Trans>Pipeline runs</Trans>,
          icon: <IconSitemap size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/settings",
          label: <Trans>Settings</Trans>,
          icon: <IconSettings size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/provider-credits",
          label: <Trans>Provider credits</Trans>,
          icon: <IconCoins size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/platform-billing",
          label: <Trans>Platform billing</Trans>,
          icon: <IconReportMoney size={16} className="lex-nav-ico" aria-hidden />,
          visible: isAdmin,
          group: "admin",
        },
        {
          to: "/audit",
          label: <Trans>Audit log</Trans>,
          icon: <IconClipboardList size={16} className="lex-nav-ico" aria-hidden />,
          visible: canViewAudit,
          group: "admin",
        },
      ]

  // The tenant "Known voices" (voiceprint library) page. Filed under "admin"
  // alongside Audit log (same owner/admin visibility). Only shown when there's an
  // active org: the page reads the active-org speakers API, so a super-admin with
  // no active org (who'd have no company context) reaches the per-company
  // enrollment page from the company-detail page instead. Voxcanon nav renders
  // labels bare, so no icon there.
  const speakersItem: INavItem | null =
    activeOrgId != null && activeOrgId !== ""
      ? {
          to: "/speakers",
          label: <Trans>Known voices</Trans>,
          icon: isVox ? undefined : <IconMicrophone size={16} className="lex-nav-ico" aria-hidden />,
          visible: canViewAudit,
          group: "admin",
        }
      : null

  // Companies is a workspace link only when "/" is the companies list. With a
  // company in scope it stays inside Administration, so it is not shown twice.
  const companiesHome: INavItem | null = companiesFirst
    ? {
        to: "/companies",
        label: <Trans>Companies</Trans>,
        icon: isVox ? undefined : <IconBuildingSkyscraper size={16} className="lex-nav-ico" aria-hidden />,
        visible: true,
        group: "workspace",
      }
    : null

  const items: INavItem[] = [
    ...(companiesHome ? [companiesHome] : []),
    ...baseItems,
    ...(speakersItem ? [speakersItem] : []),
  ]

  // Voxcanon marks the active row with `data-active` (TanStack itself only
  // sets `data-status`, so we wire it explicitly); Lexicanon uses
  // `aria-current="page"`. Both feed the existing `.{vox,lex}-nav-link` CSS.
  // "/" fuzzy-matches every route, so Meetings/Sessions match the index only.
  const renderLink = (item: INavItem): React.JSX.Element => {
    const inner = isVox ? (
      item.label
    ) : (
      <>
        {item.icon}
        <span>{item.label}</span>
      </>
    )
    const exact = item.to === "/"
    const active = exact ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`)
    return isVox ? (
      <Link
        key={item.to}
        to={item.to}
        className="vox-nav-link"
        onClick={handleNav}
        activeOptions={exact ? { exact: true } : undefined}
        activeProps={{ "data-active": "true" }}
        data-active={active ? "true" : undefined}
      >
        {inner}
      </Link>
    ) : (
      <Link
        key={item.to}
        to={item.to}
        className="lex-nav-link"
        onClick={handleNav}
        activeOptions={exact ? { exact: true } : undefined}
        activeProps={{ "aria-current": "page" }}
        aria-current={active ? "page" : undefined}
      >
        {inner}
      </Link>
    )
  }

  // Vertical = mobile drawer: full-width rows split into a workspace group and
  // an admin group under a quiet eyebrow (only when the admin group has rows).
  if (layout === "vertical") {
    const visible = items.filter((item) => item.visible)
    const workspaceItems = visible.filter((item) => item.group === "workspace")
    const adminItems = visible.filter((item) => item.group === "admin")
    return (
      <>
        <div className="lex-nav-drawer-section">{workspaceItems.map(renderLink)}</div>
        {adminItems.length > 0 && (
          <div className="lex-nav-drawer-section">
            <span className="lex-eyebrow lex-nav-drawer-eyebrow">
              <Trans>Administration</Trans>
            </span>
            {adminItems.map(renderLink)}
          </div>
        )}
      </>
    )
  }

  // Horizontal = header: workspace links inline; admin tooling collapses into
  // one "Administration ▾" menu so a super-admin's eleven destinations can
  // never wrap the header onto a second line (the drawer keeps flat rows).
  const visible = items.filter((item) => item.visible)
  const workspaceItems = visible.filter((item) => item.group === "workspace")
  const adminItems = visible.filter((item) => item.group === "admin")
  return (
    <Group gap={isVox ? "lg" : "xs"} wrap="nowrap">
      {workspaceItems.map(renderLink)}
      {adminItems.length > 0 && <AdminNavMenu items={adminItems} isVox={isVox} onNav={handleNav} />}
    </Group>
  )
}

/**
 * The header's collapsed admin section: a nav-link-styled trigger opening a
 * dropdown of the admin destinations. The trigger reads as active whenever the
 * current route lives under any of its items, mirroring how the drawer files
 * these under its "Administration" eyebrow.
 */
function AdminNavMenu({
  items,
  isVox,
  onNav,
}: {
  items: INavItem[]
  isVox: boolean
  onNav: () => void
}): React.JSX.Element {
  const { pathname } = useLocation()
  // Admin destinations are all non-root paths, so a prefix match is safe.
  const adminActive = items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))

  return (
    <Menu shadow="md" width={220} position="bottom-start" offset={6}>
      <Menu.Target>
        <button
          type="button"
          className={`${isVox ? "vox-nav-link" : "lex-nav-link"} lex-nav-menu-trigger`}
          data-active={isVox && adminActive ? "true" : undefined}
          aria-current={!isVox && adminActive ? "page" : undefined}
        >
          <span>
            <Trans>Administration</Trans>
          </span>
          <IconChevronDown size={14} className="lex-nav-chevron" aria-hidden />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        {items.map((item) => (
          <Menu.Item key={item.to} component={Link} to={item.to} onClick={onNav} leftSection={item.icon ?? undefined}>
            {item.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}
